import type { BandwidthTestResult } from './bandwidth-test';
import { BandwidthTest } from './bandwidth-test';
import { type DiagnosticSuiteResult, DiagnosticTest } from './diagnostic-test';

/**
 * Parse browser name and version from user agent string
 */
export function parseBrowserInfo(userAgent: string): string {
  if (userAgent.includes('Firefox/')) {
    const match = userAgent.match(/Firefox\/(\d+)/);
    return `Firefox ${match?.[1] || ''}`;
  }
  if (userAgent.includes('Edg/')) {
    const match = userAgent.match(/Edg\/(\d+)/);
    return `Edge ${match?.[1] || ''}`;
  }
  if (userAgent.includes('Chrome/')) {
    const match = userAgent.match(/Chrome\/(\d+)/);
    return `Chrome ${match?.[1] || ''}`;
  }
  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
    const match = userAgent.match(/Version\/(\d+)/);
    return `Safari ${match?.[1] || ''}`;
  }
  return 'Unknown';
}

/**
 * Generate a human-readable text report from diagnostic results
 */
export function generateReport(
  result: DiagnosticSuiteResult,
  bandwidthResult?: BandwidthTestResult | null
): string {
  const browserInfo = parseBrowserInfo(result.deviceInfo.userAgent);
  const networkStatus = result.networkEnvironment.captivePortalDetected
    ? 'Captive Portal Detected'
    : result.networkEnvironment.publicNetworkLikely
      ? 'Public/Shared Network Likely'
      : 'Private Network';

  let speedSection = '';
  if (bandwidthResult) {
    speedSection = `
Speed Test Results
------------------
Download Speed: ${Math.round(bandwidthResult.speedMbps)} Mbps
Peak Speed: ${Math.round(bandwidthResult.peakSpeedMbps)} Mbps
Latency (Unloaded): ${bandwidthResult.latency.unloaded}ms
Latency (Loaded): ${bandwidthResult.latency.loaded}ms
Jitter: ±${bandwidthResult.latency.jitter}ms
Bufferbloat: ${bandwidthResult.bufferbloatRatio}x
Downloaded: ${BandwidthTest.formatBytes(bandwidthResult.downloadedBytes)}
Duration: ${(bandwidthResult.testDurationMs / 1000).toFixed(1)}s

`;
  }

  return `
Network Diagnostic Report
========================
Generated: ${result.timestamp}

Device Information
------------------
Browser: ${browserInfo}
Platform: ${result.deviceInfo.platform}
Screen: ${result.deviceInfo.screenWidth}x${result.deviceInfo.screenHeight} @${result.deviceInfo.devicePixelRatio}x
Timezone: ${result.deviceInfo.timezone}
CPU Cores: ${result.deviceInfo.hardwareConcurrency || 'N/A'}
Memory: ${result.deviceInfo.deviceMemory ? `${result.deviceInfo.deviceMemory} GB` : 'N/A'}
${result.deviceInfo.connection ? `Speed Rating: ${result.deviceInfo.connection.effectiveType.toUpperCase()}\nEstimated Bandwidth: ${result.deviceInfo.connection.downlink} Mbps\nEstimated RTT: ${result.deviceInfo.connection.rtt}ms` : ''}

Network Environment
-------------------
Status: ${networkStatus}
${result.networkEnvironment.indicators.length > 0 ? `Indicators:\n${result.networkEnvironment.indicators.map((indicator) => `  - ${indicator}`).join('\n')}` : 'No special indicators detected.'}

${speedSection}Test Results
------------
Test A (Baseline - Single Connection):
  Goodput: ${Math.round(result.testA.goodputOverallMbps)} Mbps
  Files: ${result.testA.fileCount}
  Duration: ${DiagnosticTest.formatDuration(result.testA.totalDurationMs)}
  TTFB (p50): ${result.testA.aggregated.p50Ttfb}ms

Test B (Parallel - ${result.testB.concurrency} Connections):
  Goodput: ${Math.round(result.testB.goodputOverallMbps)} Mbps
  Files: ${result.testB.fileCount}
  Duration: ${DiagnosticTest.formatDuration(result.testB.totalDurationMs)}
  TTFB (p50): ${result.testB.aggregated.p50Ttfb}ms

Test C (Small Files Burst - ${result.testC.fileCount} files):
  TTFB (p50): ${result.testC.aggregated.p50Ttfb}ms
  TTFB (p95): ${result.testC.aggregated.p95Ttfb}ms
  Duration: ${DiagnosticTest.formatDuration(result.testC.totalDurationMs)}
  Note: Throughput not meaningful for latency-focused test

Test D (Sustained - ${DiagnosticTest.formatDuration(result.testD.totalDurationMs)}):
  Goodput: ${Math.round(result.testD.goodputOverallMbps)} Mbps
  Downloaded: ${DiagnosticTest.formatBytes(result.testD.totalBytes)}
  Files: ${result.testD.fileCount}

Protocol & Connection Analysis
------------------------------
${result.analysis.protocolSummary || 'Protocol data not available'}
Overall Confidence: ${result.analysis.overallConfidence || 'N/A'}

Connection Limit Analysis:
  Detected: ${result.analysis.connectionLimit?.detected ? 'YES' : 'No'}
  Confidence: ${result.analysis.connectionLimit?.confidence || 'N/A'}
  Baseline Goodput: ${result.analysis.connectionLimit?.baselineGoodputMbps?.toFixed(1) || 'N/A'} Mbps
  Parallel Goodput: ${result.analysis.connectionLimit?.parallelGoodputMbps?.toFixed(1) || 'N/A'} Mbps
  Goodput Ratio: ${result.analysis.connectionLimit?.goodputRatio?.toFixed(2) || 'N/A'}
  Hostnames Used: ${result.analysis.connectionLimit?.hostnamesUsed || 'N/A'}
  Reason: ${result.analysis.connectionLimit?.reason || 'N/A'}

Inspection Overhead Analysis:
  Detected: ${result.analysis.inspectionOverhead?.detected ? 'YES' : 'No'}
  Confidence: ${result.analysis.inspectionOverhead?.confidence || 'N/A'}
  TTFB P50: ${result.analysis.inspectionOverhead?.smallFileTtfbP50?.toFixed(1) || 'N/A'}ms
  TTFB P95: ${result.analysis.inspectionOverhead?.smallFileTtfbP95?.toFixed(1) || 'N/A'}ms
  Tail Latency Ratio: ${result.analysis.inspectionOverhead?.ttfbTailLatencyRatio?.toFixed(2) || 'N/A'}
  Reason: ${result.analysis.inspectionOverhead?.reason || 'N/A'}

Throttling Analysis:
  Detected: ${result.analysis.throttling?.detected ? 'YES' : 'No'}
  Confidence: ${result.analysis.throttling?.confidence || 'N/A'}
  First Half: ${result.analysis.throttling?.firstHalfThroughputMbps?.toFixed(1) || 'N/A'} Mbps
  Second Half: ${result.analysis.throttling?.secondHalfThroughputMbps?.toFixed(1) || 'N/A'} Mbps
  Degradation Ratio: ${result.analysis.throttling?.degradationRatio?.toFixed(2) || 'N/A'}
  Reason: ${result.analysis.throttling?.reason || 'N/A'}

Summary
-------
Connection Limits: ${result.analysis.connectionLimitDetected ? 'DETECTED' : 'None'}
Inspection Overhead: ${result.analysis.inspectionOverheadDetected ? 'DETECTED' : 'None'}
Throttling: ${result.analysis.throttlingDetected ? 'DETECTED' : 'None'}

${result.analysis.issues.length > 0 ? `Issues:\n${result.analysis.issues.map((issueItem) => `  - ${issueItem}`).join('\n')}` : 'No issues detected.'}

${result.analysis.recommendations.length > 0 ? `Recommendations:\n${result.analysis.recommendations.map((recItem) => `  - ${recItem}`).join('\n')}` : ''}

User Agent: ${result.deviceInfo.userAgent}
`.trim();
}
